import { readActorSyncRefusal } from "@-/dev.improve.haniverse.lib.database.engine/sync-refusal";
import { isTransientNetworkError } from "@-/dev.improve.haniverse.lib.error-classification";

/**
 * What every sync failure means and what happens next, without the user (see `database.md`,
 * "Device: sync supervisor"). The classifier is total: there is no failure it cannot place, so
 * there is none without a next step.
 *
 * | Class | Examples | What happens |
 * |---|---|---|
 * | `transient` | offline, timeout, the server or Turso unavailable | The sync loop tries again. |
 * | `server-behind` | the app has a migration the server does not have yet | The loop tries again; the server is deploying. |
 * | `replay-failed` | a download could not replay an unsent change | The next cycle uploads first, then downloads. |
 * | `update-required` | the server no longer speaks this app's protocol | Automatic sync rests; study continues; an app update ends it. |
 * | `credential` | the device credential was refused | A new credential is requested, and the replica is kept. |
 * | `database-moved` | the account's cloud database was replaced | As `credential`: the new credential names the new database. |
 * | `too-large` | one change is larger than the server accepts | Reported. The transport already splits every upload it can. |
 * | `engine-fault` | an engine call that never answered, anything else | The loop tries again; after repeated failures the engine is reopened, and when faults continue after a reopen, the replica is rebuilt. |
 */
export type ActorSyncFailureClass =
  | "credential"
  | "database-moved"
  | "engine-fault"
  | "replay-failed"
  | "server-behind"
  | "too-large"
  | "transient"
  | "update-required";

const REPLAY_FAILED = /failed to replay local change/i;
const MAX_CAUSE_DEPTH = 5;

function findInCauses<T>(error: unknown, read: (error: unknown) => T | null): T | null {
  let current: unknown = error;
  for (let depth = 0; current !== undefined && depth < MAX_CAUSE_DEPTH; depth += 1) {
    const found = read(current);
    if (found !== null) {
      return found;
    }
    current = current instanceof Error ? current.cause : undefined;
  }
  return null;
}

/** Place any failure of a push or a pull. Anything unrecognised is an engine fault. */
export function classifyActorSyncFailure(error: unknown): ActorSyncFailureClass {
  const refusal = findInCauses(error, readActorSyncRefusal);
  if (refusal !== null) {
    // The gateway answered: its reason is the classification.
    return refusal === "unavailable" ? "transient" : refusal;
  }
  const replayFailed = findInCauses(error, (cause) =>
    cause instanceof Error && REPLAY_FAILED.test(cause.message) ? true : null,
  );
  if (replayFailed === true) {
    return "replay-failed";
  }
  // An engine call that outlived its deadline says "timed out", but it is the engine, not the
  // cloud: as `transient` it never escalated, and a stall that does not heal kept sync dead behind
  // "The cloud could not be reached. Sync will resume by itself".
  const engineStalled = findInCauses(error, (cause) =>
    cause instanceof Error && cause.name === "DatabaseEngineStalledError" ? true : null,
  );
  if (engineStalled === true) {
    return "engine-fault";
  }
  return isTransientNetworkError(error) ? "transient" : "engine-fault";
}

/** What the user is told. Every message says where their study is, because that is the worry. */
export function describeActorSyncFailure(failureClass: ActorSyncFailureClass): string {
  switch (failureClass) {
    case "credential":
    case "database-moved": {
      return "This device is reconnecting to your account. Your study is saved on this device.";
    }
    case "server-behind": {
      return "Haniverse is updating its servers. Sync will resume by itself; your study is saved on this device.";
    }
    case "too-large": {
      return "One change is too large to upload. Your study is saved on this device; contact support if this stays.";
    }
    case "transient": {
      return "The cloud could not be reached. Sync will resume by itself; your study is saved on this device.";
    }
    case "update-required": {
      return "Update Haniverse to keep syncing. Your study is saved on this device.";
    }
    case "engine-fault":
    case "replay-failed": {
      return "Sync hit a problem and will try again by itself. Your study is saved on this device.";
    }
  }
}

/** How long automatic sync rests when only an app update or a sign-in can change the answer. */
const REST_MS = 15 * 60_000;
/** Consecutive engine faults before the engine is reopened. */
const ENGINE_FAULTS_BEFORE_REOPEN = 3;
/** At most one reopen per this long: a fault that survives a reopen is not fixed by another. */
const REOPEN_INTERVAL_MS = 5 * 60_000;
/**
 * At most one rebuild per this long. A rebuild downloads the whole account again, and one that
 * does not fix the fault points at the cloud copy or the engine, which a second rebuild does not
 * change either.
 */
const REBUILD_INTERVAL_MS = 60 * 60_000;

export type ActorSyncSupervisorTransition =
  | { kind: "rebuild"; reason: "faults-after-reopen" | "reopen-failed" }
  | { failureClass: "credential" | "database-moved"; kind: "refresh-credential" }
  | { faults: number; kind: "reopen" }
  | { failureClass: "credential" | "update-required"; kind: "rest"; untilMs: number };

export interface ActorSyncSupervisor {
  /** Whether automatic sync may start a cycle now. A manual sync always may. */
  canRunAutomatically(): boolean;
  /** A push or pull failed. Returns its class and starts the automatic response, if any. */
  failed(error: unknown): ActorSyncFailureClass;
  /** A push or pull completed. */
  succeeded(): void;
}

/**
 * The response policy for one sync connection. It holds no stored state: a rest is a point in
 * time that passes by itself, never a flag that something has to clear.
 */
export function createActorSyncSupervisor({
  now = Date.now,
  onError,
  onTransition,
  rebuild,
  refreshCredential,
  reopen,
}: {
  now?: () => number;
  onError?: (message: string, error: unknown) => void;
  /** Every automatic response, as it starts: what the device is doing about sync, for the logs. */
  onTransition?: (transition: ActorSyncSupervisorTransition) => void;
  /**
   * Replace the replica itself: carry its unsent changes to the cloud through the import, then
   * download it fresh. The response once a reopen has not helped, or cannot be done.
   */
  rebuild: () => Promise<void>;
  /**
   * Ask the server for a new credential and reopen with it. Resolves `false` when the device is
   * no longer signed in, in which case only signing in again helps.
   */
  refreshCredential: () => Promise<boolean>;
  /** Replace the engine with a fresh one on the same replica. */
  reopen: () => Promise<void>;
}): ActorSyncSupervisor {
  let restUntil = 0;
  let refreshing = false;
  let engineFaults = 0;
  let lastReopenAt = Number.NEGATIVE_INFINITY;
  let lastRebuildAt = Number.NEGATIVE_INFINITY;
  // Set by a reopen, cleared by a success: faults that reach the threshold again after a reopen
  // did not come from the engine instance, so the replica is replaced next.
  let reopenedSinceSuccess = false;

  const rebuildNow = (reason: "faults-after-reopen" | "reopen-failed"): void => {
    if (now() - lastRebuildAt < REBUILD_INTERVAL_MS) {
      return;
    }
    lastRebuildAt = now();
    reopenedSinceSuccess = false;
    onTransition?.({ kind: "rebuild", reason });
    void rebuild().catch((rebuildError: unknown) => {
      onError?.("Rebuilding the replica failed", rebuildError);
    });
  };

  const refresh = (failureClass: "credential" | "database-moved"): void => {
    if (refreshing) {
      return;
    }
    refreshing = true;
    onTransition?.({ failureClass, kind: "refresh-credential" });
    void refreshCredential()
      .then((signedIn) => {
        if (!signedIn) {
          restUntil = now() + REST_MS;
          onTransition?.({ failureClass: "credential", kind: "rest", untilMs: restUntil });
        }
      })
      .catch((error: unknown) => {
        // The next refused request asks again; nothing is held.
        onError?.("Refreshing the sync credential failed", error);
      })
      .finally(() => {
        refreshing = false;
      });
  };

  return {
    canRunAutomatically: () => !refreshing && now() >= restUntil,
    failed: (error) => {
      const failureClass = classifyActorSyncFailure(error);
      if (failureClass !== "engine-fault") {
        engineFaults = 0;
      }
      switch (failureClass) {
        case "credential":
        case "database-moved": {
          refresh(failureClass);
          break;
        }
        case "engine-fault": {
          engineFaults += 1;
          if (engineFaults < ENGINE_FAULTS_BEFORE_REOPEN) {
            break;
          }
          if (reopenedSinceSuccess) {
            // A fresh engine on the same replica did not help: replace the replica.
            engineFaults = 0;
            rebuildNow("faults-after-reopen");
            break;
          }
          if (now() - lastReopenAt >= REOPEN_INTERVAL_MS) {
            const faults = engineFaults;
            engineFaults = 0;
            lastReopenAt = now();
            reopenedSinceSuccess = true;
            onTransition?.({ faults, kind: "reopen" });
            void reopen().catch((reopenError: unknown) => {
              onError?.("Reopening the sync engine failed", reopenError);
              // The replica could not even be reopened: rebuild it.
              rebuildNow("reopen-failed");
            });
          }
          break;
        }
        case "update-required": {
          restUntil = now() + REST_MS;
          onTransition?.({ failureClass, kind: "rest", untilMs: restUntil });
          break;
        }
        case "replay-failed":
        case "server-behind":
        case "too-large":
        case "transient": {
          break;
        }
      }
      return failureClass;
    },
    succeeded: () => {
      engineFaults = 0;
      reopenedSinceSuccess = false;
      restUntil = 0;
    },
  };
}
